import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, Calendar, MoreVertical, CheckCircle2, Ban, Receipt as ReceiptIcon,
  History as HistoryIcon, QrCode, Send, RotateCcw, ShieldOff, AlertTriangle,
} from 'lucide-react';
import { useUpdateBooking,
  type Booking, BOOKING_STATUS, BOOKING_STATUS_PENDING, BOOKING_STATUS_CONFIRMED,
  BOOKING_STATUS_COMPLETED, BOOKING_STATUS_CANCELLED, BOOKING_STATUS_NO_SHOW } from '../../hooks/useBookings';
import { useCapabilities } from '../../hooks/useCapabilities';
import { VOID_PAYMENT, REFUND_PAYMENT } from '../../utils/capabilities';
import {
  useBookingCancellationPolicy, useCancelBookingOnBehalf, useVoidBooking, useRefundBooking,
  useMarkBookingNoShow, useBookingReceipt, useResendBookingReceipt, useReissueBookingAccess,
  useRevokeBookingAccess, useBookingHistory, isBookingOpsUnavailable,
} from '../../hooks/useBookingOps';
import { validateRefundInput } from './refundValidation';
import { bookingPaymentActions } from './paymentActions';
import { diffSummary } from './changelogDiff';
import type { ReceiptLineItem } from '../../api/bookingOps';
import { describeBookingLine, type BookingLineContext } from './bookingLineDisplay';
import { useServiceById } from '../../hooks/useServices';
import { useBusinessAccessInvitations } from '../../hooks/useBusinessAccessInvitations';
import { passActivityLink } from '../../api/businessAccessCombined';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { worksAsFrontDesk } from '../../utils/frontDeskMatrix';

// SAM-590 — a receipt line's `label` is a flat string for every existing line type, but an
// "add_on" line (PR #419) sends a bilingual `{en, es}` object instead. This repo's admin UI is
// English-first (no i18n switch on this page today), so `en` is preferred, falling back to `es`
// and then the line's own index-based placeholder, same as the pre-SAM-590 `li.label || ...`.
function receiptLineLabel(li: ReceiptLineItem, fallback: string): string {
  if (typeof li.label === 'string' && li.label) return li.label;
  if (li.label && typeof li.label === 'object') return li.label.en || li.label.es || fallback;
  return fallback;
}

// SAM-590 — an add_on line carries `subtotal` instead of `total` (see ReceiptLineItem's comment).
function receiptLineAmount(li: ReceiptLineItem): number | null {
  return li.subtotal ?? li.total ?? null;
}

interface BookingDetailPanelProps {
  booking: Booking;
  onClose: () => void;
}

// Keyed off the shared constants, not bare numbers: a literal { 1: ..., 2: ..., 3: ..., 4: ... }
// here had 3 (completed) and 4 (cancelled) the wrong way round, same as every other copy of this
// mapping this file has carried — see the comment below. It showed up as the header chip's color:
// a cancelled booking got the green "confirmed/completed" bk-status-completed class (--ok-bg)
// instead of bk-status-cancelled (--bad-bg/--danger), while the list dot next to it (driven by
// BOOKING_STATUS's own dotClass, not this map) correctly showed red — the two disagreed.
const STATUS_CLASS: Record<number, string> = {
  [BOOKING_STATUS_PENDING]: 'bk-status-pending',
  [BOOKING_STATUS_CONFIRMED]: 'bk-status-confirmed',
  [BOOKING_STATUS_COMPLETED]: 'bk-status-completed',
  [BOOKING_STATUS_CANCELLED]: 'bk-status-cancelled',
  [BOOKING_STATUS_NO_SHOW]: 'bk-status-no-show',
};
// See BOOKING_STATUS in useBookings: one definition, because three of the four copies of this had
// 3 and 4 the other way round and the <select> below WROTE the id.
const STATUS_LABEL: Record<number, string> = Object.fromEntries(
  BOOKING_STATUS.map((s) => [s.id, s.label]),
);

function formatDate(d?: string) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return d; }
}
function formatYear(d?: string) {
  if (!d) return '';
  try { return String(new Date(d).getFullYear()); } catch { return ''; }
}
function formatTime(d?: string) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}
function formatDateTime(d?: string) {
  if (!d) return '';
  try {
    const date = new Date(d);
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
  } catch { return d; }
}
function durationLabel(start: string, end: string): string {
  try {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const min = Math.round(ms / 60000);
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h}h ${m}min` : `${h} hour${h === 1 ? '' : 's'}`;
  } catch { return ''; }
}
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';
}

type DialogState =
  | { type: 'cancel' }
  | { type: 'complete' }
  | { type: 'no_show' }
  | { type: 'void' }
  | { type: 'refund' }
  | null;

type Tab = 'details' | 'receipt' | 'changelog';

export const BookingDetailPanel: React.FC<BookingDetailPanelProps> = ({ booking, onClose }) => {
  const updateBooking = useUpdateBooking();
  const cancelOnBehalf = useCancelBookingOnBehalf();
  const voidBooking = useVoidBooking();
  const refundBooking = useRefundBooking();
  const markNoShow = useMarkBookingNoShow();
  const resendReceipt = useResendBookingReceipt();
  const reissueAccess = useReissueBookingAccess();
  const revokeAccess = useRevokeBookingAccess();

  const [statusId, setStatusId] = useState(booking.status_id);
  const [notes, setNotes] = useState(booking.notes || '');
  const [tab, setTab] = useState<Tab>('details');
  const [dialog, setDialog] = useState<DialogState>(null);
  const [reason, setReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundError, setRefundError] = useState<string | null>(null);

  useEffect(() => {
    setStatusId(booking.status_id);
    setNotes(booking.notes || '');
    setTab('details');
    setDialog(null);
    setReason('');
    setRefundAmount('');
    setRefundError(null);
  }, [booking.id]);

  const { hasCapability, isLoading: capabilitiesLoading } = useCapabilities();
  // Tenant Front Desk and estate group roles: receipt, changelog, QR reissue/invalidate and no-show answer 403 for
  // them (contract).
  const asFrontDesk = worksAsFrontDesk(useUserPermissions());

  const customer =
    booking.user_name ||
    booking.user_email ||
    `Guest ${booking.main_user_id}`;
  const customerInitials = initials(customer);

  // SAM-624 — labels for the stored ids (rate group, cabana) come from the booking's service
  // (cached description endpoint); the ids are shown only until/unless it is available.
  const rawService: any = booking.service_id;
  const serviceDetail = useServiceById(typeof rawService === 'object' ? rawService?.id : rawService).data as any;
  const lineCtx: BookingLineContext = {
    rateGroupLabel: (id) => {
      const g = serviceDetail?.rate_groups?.find((x: any) => String(x.id) === id);
      return g?.label?.en || g?.label?.es || null;
    },
    resourceName: (key) => {
      const r = serviceDetail?.settings?.resources?.find((x: any) => x.key === key);
      return r?.name || null;
    },
  };

  const options: Array<any> = Array.isArray(booking.booking_options) ? booking.booking_options : [];
  const totalGuests = options.reduce((s, o) => s + (o.total_guest || 0), 0);

  // What the CLIENT sent, kept only as the fallback for bookings written before SAM-193.
  const optionsTotal = options.reduce((s, o) => s + (o.total_amount || 0), 0);
  // What the server actually charged (pricing.py). This is the number on the receipt; the sum
  // above is the client's arithmetic and the two can disagree.
  const chargedAmount = booking.amount != null ? Number(booking.amount) : null;
  const totalAmount = chargedAmount ?? optionsTotal;
  const amountIsServerPriced = chargedAmount != null;
  const currency = booking.currency || options[0]?.currency || 'USD';
  const tax = booking.tax != null ? Number(booking.tax) : null;

  // sam_core.booking_status: 1 pending, 2 confirmed, 3 completed, 4 cancelled, 5 no_show.
  // These three were 3=cancelled / 4=completed, the same inversion STATUS_MAP had, so this panel
  // disabled "Cancel" on a completed booking and offered it on a cancelled one.
  const isCompleted = booking.status_id === BOOKING_STATUS_COMPLETED;
  const isCancelled = booking.status_id === BOOKING_STATUS_CANCELLED;
  const isNoShow = booking.status_id === BOOKING_STATUS_NO_SHOW;
  const isLocked = isCancelled || isCompleted || isNoShow;

  const dirty = statusId !== booking.status_id || notes !== (booking.notes || '');
  const isSaving = updateBooking.isPending;

  // SAM-553 — VOID/REFUND gating. There is no dedicated REFUND_BOOKINGS capability on the backend:
  // utils/capabilities.ts already mirrors the SAM-538 matrix (capability_matrix.py) with
  // VOID_PAYMENT/REFUND_PAYMENT granted to the `admin` (tenant-wide) and `business_admin`
  // (business-scoped) Cognito groups — the exact "derive it from business_admin/admin groups"
  // fallback this feature was scoped to build, already built for the Payments page's own
  // void/refund controls (see PaymentDetailDrawer.tsx / useVoidPayment.ts). Reusing it here keeps
  // one source of truth instead of a second, drifting copy of the same group check in
  // useUserPermissions.ts. `targetBusinessId` is the BOOKING's own business (not the caller's
  // current business-context selection), which matters for an admin viewing "All Businesses".
  const canVoid = !capabilitiesLoading && hasCapability(VOID_PAYMENT, booking.business_id);
  const canRefund = !capabilitiesLoading && hasCapability(REFUND_PAYMENT, booking.business_id);

  // Is there a charge/auth on file at all, and which action fits its state (SAM-557: `paid` on a
  // booking is an uncaptured authorization, so it gets Void, never Refund).
  const hasChargeOnFile = !!(booking.payment_gateway || booking.payment_reference || chargedAmount);
  const paymentActions = bookingPaymentActions(booking.payment_status);
  const showVoidButton = canVoid && hasChargeOnFile && paymentActions.void;
  const showRefundButton = canRefund && hasChargeOnFile && paymentActions.refund;

  // SAM-608 — the pass(es) this booking granted: /business_access/combined filtered by
  // booking_id (business_access.booking_id), scoped to the booking's business.
  const linkedPassesQuery = useBusinessAccessInvitations(
    { entity: 'business_access', booking_id: String(booking.id), business_id: booking.business_id, limit: 10 },
    { enabled: !!booking.id },
  );
  const linkedPasses = (linkedPassesQuery.data?.data || []).filter(
    (p) => String(p.booking_id ?? p.booking?.id ?? '') === String(booking.id),
  );

  const cancelPolicy = useBookingCancellationPolicy(booking.id, dialog?.type === 'cancel');
  const bookingReceipt = useBookingReceipt(tab === 'receipt' ? booking.id : null);
  const bookingHistory = useBookingHistory(tab === 'changelog' ? booking.id : null);

  const handleSave = () => {
    updateBooking.mutate(
      { id: booking.id, data: { status_id: Number(statusId), notes } },
      { onSuccess: () => onClose() },
    );
  };

  const handleStatusChange = (newId: number) => {
    updateBooking.mutate({ id: booking.id, data: { status_id: newId } });
    setStatusId(newId);
    setDialog(null);
  };

  const closeDialog = () => {
    setDialog(null);
    setReason('');
    setRefundAmount('');
    setRefundError(null);
  };

  const handleCancelOnBehalf = () => {
    if (!reason.trim()) return;
    cancelOnBehalf.mutate(
      { bookingId: booking.id, reason: reason.trim() },
      { onSuccess: () => { closeDialog(); onClose(); } },
    );
  };

  const handleVoid = () => {
    if (!reason.trim()) return;
    voidBooking.mutate({ bookingId: booking.id, reason: reason.trim() }, { onSuccess: closeDialog });
  };

  const handleRefund = () => {
    const result = validateRefundInput(refundAmount, chargedAmount, reason);
    if (!result.valid) {
      setRefundError(result.error || 'Invalid input');
      return;
    }
    refundBooking.mutate(
      { bookingId: booking.id, reason: reason.trim(), amount: result.amount },
      { onSuccess: closeDialog },
    );
  };

  const handleNoShow = () => {
    markNoShow.mutate(
      { bookingId: booking.id, reason: reason.trim() || undefined },
      { onSuccess: closeDialog },
    );
  };

  return (
    <div className="bk-detail-inner">
      {/* Header */}
      <header className="bk-detail-header">
        <div className="bk-detail-title-block">
          <div className="bk-detail-eyebrow">
            <span className={`bk-status-badge ${STATUS_CLASS[booking.status_id] || ''}`}>
              {booking.status_id === 2 && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {STATUS_LABEL[booking.status_id] || 'Unknown'}
            </span>
            {booking.confirmation_code && (
              <button
                type="button"
                className="bk-code-pill"
                title="Copy confirmation code"
                onClick={() => navigator.clipboard?.writeText(booking.confirmation_code!)}
              >
                {booking.confirmation_code}
              </button>
            )}
          </div>
          <h1 className="bk-detail-title">{customer}</h1>
          <div className="bk-detail-subtitle">
            {booking.service_name || 'Service'}
            {booking.business_name && ` at ${booking.business_name}`}
          </div>
        </div>

        <div className="bk-detail-actions">
          {booking.user_email && (
            <a href={`mailto:${booking.user_email}`} className="bk-icon-btn" title="Email guest">
              <Mail />
            </a>
          )}
          <button className="bk-icon-btn" title="View calendar" type="button">
            <Calendar />
          </button>
          <button className="bk-icon-btn" title="Close" type="button" onClick={onClose}>
            <MoreVertical />
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bk-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'details'} className={`bk-tab ${tab === 'details' ? 'is-active' : ''}`} onClick={() => setTab('details')}>
          Details
        </button>
        {!asFrontDesk && (<>
        <button type="button" role="tab" aria-selected={tab === 'receipt'} className={`bk-tab ${tab === 'receipt' ? 'is-active' : ''}`} onClick={() => setTab('receipt')}>
          <ReceiptIcon size={14} /> Receipt
        </button>
        <button type="button" role="tab" aria-selected={tab === 'changelog'} className={`bk-tab ${tab === 'changelog' ? 'is-active' : ''}`} onClick={() => setTab('changelog')}>
          <HistoryIcon size={14} /> Changelog
        </button>
        </>)}
      </div>

      {tab === 'receipt' && (
        <section className="bk-section">
          {bookingReceipt.isLoading ? (
            <div className="bk-tab-empty">Loading receipt…</div>
          ) : isBookingOpsUnavailable(bookingReceipt.error) ? (
            <div className="bk-tab-empty">Receipt is not available yet.</div>
          ) : bookingReceipt.isError ? (
            <div className="bk-tab-empty">Could not load the receipt.</div>
          ) : bookingReceipt.data ? (
            <>
              <div className="bk-options">
                <div className="bk-options-row header">
                  <span>Item</span>
                  <span className="bk-opt-num">Qty</span>
                  <span className="bk-opt-num">Unit</span>
                  <span className="bk-opt-num">Total</span>
                </div>
                {bookingReceipt.data.line_items
                  .filter((li) => li.type !== 'add_on')
                  .map((li, i) => (
                    <div className="bk-options-row" key={i}>
                      <div>
                        <div className="bk-opt-name">
                          {li.type === 'unit' ? describeBookingLine(li, i, lineCtx).title : receiptLineLabel(li, `Item ${i + 1}`)}
                        </div>
                        {describeBookingLine(li, i, lineCtx).details.map((d) => <div className="bk-opt-name-sub" key={d}>{d}</div>)}
                      </div>
                      <span className="bk-opt-num" style={{ whiteSpace: 'nowrap' }}>{describeBookingLine(li, i, lineCtx).quantityText}</span>
                      <span className="bk-opt-num">{li.unit_price != null ? `$${Number(li.unit_price).toFixed(2)}` : '—'}</span>
                      <span className="bk-opt-num">{receiptLineAmount(li) != null ? `$${Number(receiptLineAmount(li)).toFixed(2)}` : '—'}</span>
                    </div>
                  ))}
                <div className="bk-options-row total">
                  <span className="bk-opt-name">Total</span>
                  <span></span>
                  <span></span>
                  <span className="bk-opt-num">${Number(bookingReceipt.data.total).toFixed(2)} {bookingReceipt.data.currency || currency}</span>
                </div>
              </div>

              {/* SAM-590 — add-on lines get their own subheading rather than being mixed into the
                  main item table, so "what did the guest add on top of the base booking" reads at
                  a glance. */}
              {bookingReceipt.data.line_items.some((li) => li.type === 'add_on') && (
                <div className="bk-section" style={{ marginTop: 16 }}>
                  <div className="bk-section-header"><span className="bk-section-title">Add-ons</span></div>
                  <div className="bk-options">
                    {bookingReceipt.data.line_items
                      .filter((li) => li.type === 'add_on')
                      .map((li, i) => (
                        <div className="bk-options-row" key={i}>
                          <span className="bk-opt-name">{receiptLineLabel(li, `Add-on ${i + 1}`)}</span>
                          <span className="bk-opt-num">{li.quantity ?? '—'}</span>
                          <span className="bk-opt-num">{li.unit_price != null ? `$${Number(li.unit_price).toFixed(2)}` : '—'}</span>
                          <span className="bk-opt-num">{receiptLineAmount(li) != null ? `$${Number(receiptLineAmount(li)).toFixed(2)}` : '—'}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {bookingReceipt.data.movements.length > 0 && (
                <div className="bk-section" style={{ marginTop: 16 }}>
                  <div className="bk-section-header"><span className="bk-section-title">Movements</span></div>
                  <div className="bk-timeline">
                    {bookingReceipt.data.movements.map((m, i) => (
                      <div className="bk-tl-item" key={i}>
                        <span className="bk-tl-dot muted" />
                        <span className="bk-tl-label">{m.type} — ${Number(m.amount).toFixed(2)} {m.currency || currency}</span>
                        <span className="bk-tl-time">{formatDateTime(m.created_at || undefined)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                className="bk-btn bk-btn-secondary"
                style={{ marginTop: 16 }}
                disabled={resendReceipt.isPending}
                onClick={() => resendReceipt.mutate(booking.id)}
              >
                <Send size={14} /> {resendReceipt.isPending ? 'Resending…' : 'Resend receipt'}
              </button>
            </>
          ) : (
            <div className="bk-tab-empty">No receipt on file.</div>
          )}
        </section>
      )}

      {tab === 'changelog' && (
        <section className="bk-section">
          {bookingHistory.isLoading ? (
            <div className="bk-tab-empty">Loading history…</div>
          ) : isBookingOpsUnavailable(bookingHistory.error) ? (
            <div className="bk-tab-empty">Changelog is not available yet.</div>
          ) : bookingHistory.isError ? (
            <div className="bk-tab-empty">Could not load the changelog.</div>
          ) : !bookingHistory.data || bookingHistory.data.length === 0 ? (
            <div className="bk-tab-empty">No changes recorded.</div>
          ) : (
            <div className="bk-changelog">
              {bookingHistory.data.map((entry) => (
                <div className="bk-changelog-row" key={entry.id}>
                  <div className="bk-changelog-top">
                    <span className="bk-changelog-action">{entry.action}</span>
                    <span className="bk-tl-time">{formatDateTime(entry.created_at)}</span>
                  </div>
                  <div className="bk-changelog-meta">
                    {entry.actor_kind || 'user'}{entry.actor_user_id ? ` #${entry.actor_user_id}` : ''}
                    {entry.reason && <> · {entry.reason}</>}
                  </div>
                  {diffSummary(entry.before, entry.after).map((line, i) => (
                    <div className="bk-changelog-diff" key={i}>{line}</div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'details' && (
        <>
          {/* Facts ribbon */}
          <div className="bk-facts">
            <div className="bk-fact">
              <div className="bk-fact-label">Date</div>
              <div className="bk-fact-value">{formatDate(booking.reservation_start) || '—'}</div>
              <div className="bk-fact-sub">{formatYear(booking.reservation_start)}</div>
            </div>
            <div className="bk-fact">
              <div className="bk-fact-label">Time</div>
              <div className="bk-fact-value">
                {formatTime(booking.reservation_start)}
                {booking.reservation_end && ` — ${formatTime(booking.reservation_end)}`}
              </div>
              <div className="bk-fact-sub">{durationLabel(booking.reservation_start, booking.reservation_end)}</div>
            </div>
            <div className="bk-fact">
              <div className="bk-fact-label">Guests</div>
              <div className="bk-fact-value">{totalGuests || '—'}</div>
              <div className="bk-fact-sub">{totalGuests === 1 ? 'Adult' : `${totalGuests} total`}</div>
            </div>
            <div className="bk-fact">
              <div className="bk-fact-label">Total</div>
              <div className="bk-fact-value">${totalAmount.toFixed(2)} {currency}</div>
              {/* payment_status is what the payment flow recorded; `payment` is a legacy boolean that
                  only ever said "was a payment required". Showing the boolean made a failed payment
                  and a free booking look identical. */}
              <div className={`bk-fact-sub ${booking.payment_status === 'paid' ? 'bk-fact-sub-paid' : ''}`}>
                {booking.payment_status
                  ? booking.payment_status.replace(/_/g, ' ')
                  : booking.payment ? 'Payment required' : 'Not required'}
              </div>
            </div>
          </div>

          {/* Guest */}
          {(booking.user_name || booking.user_email) && (
            <section className="bk-section">
              <div className="bk-section-header">
                <span className="bk-section-title">Guest</span>
              </div>
              <div className="bk-guest-card">
                <div className="bk-avatar">{customerInitials}</div>
                <div className="bk-guest-info">
                  <div className="bk-guest-name">{customer}</div>
                  <div className="bk-guest-meta">
                    {booking.user_email && (
                      <a href={`mailto:${booking.user_email}`}>{booking.user_email}</a>
                    )}
                    {booking.metadata?.phone && (
                      <>
                        <span>·</span>
                        <a href={`tel:${booking.metadata.phone}`}>{booking.metadata.phone}</a>
                      </>
                    )}
                    {booking.metadata?.reserved_by && (
                      <>
                        <span>·</span>
                        <span>Reserved by {booking.metadata.reserved_by}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="bk-guest-quick">
                  {booking.user_email && (
                    <a href={`mailto:${booking.user_email}`} className="bk-icon-btn" title="Email">
                      <Mail />
                    </a>
                  )}
                  {booking.metadata?.phone && (
                    <a href={`tel:${booking.metadata.phone}`} className="bk-icon-btn" title="Call">
                      <Phone />
                    </a>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Options */}
          {options.length > 0 && (
            <section className="bk-section">
              <div className="bk-section-header">
                <span className="bk-section-title">Booking breakdown</span>
              </div>
              <div className="bk-options">
                <div className="bk-options-row header">
                  <span>Item</span>
                  <span className="bk-opt-num">Qty</span>
                  <span className="bk-opt-num">Unit</span>
                  <span className="bk-opt-num">Subtotal</span>
                </div>
                {options.map((opt: any, i: number) => {
                  const line = describeBookingLine(opt, i, lineCtx);
                  return (
                    <div className="bk-options-row" key={i}>
                      <div>
                        <div className="bk-opt-name">{line.title}</div>
                        {opt.short_desc_en && line.kind === 'guest' && <div className="bk-opt-name-sub">{opt.short_desc_en}</div>}
                        {line.details.map((d) => <div className="bk-opt-name-sub" key={d}>{d}</div>)}
                      </div>
                      <span className="bk-opt-num" style={{ whiteSpace: 'nowrap' }}>{line.quantityText}</span>
                      <span className="bk-opt-num">${Number(opt.price ?? 0).toFixed(2)}</span>
                      <span className="bk-opt-num">${Number(opt.total_amount ?? 0).toFixed(2)}</span>
                    </div>
                  );
                })}
                {tax != null && tax > 0 && (
                  <div className="bk-options-row">
                    <span className="bk-opt-name">Tax</span>
                    <span></span>
                    <span></span>
                    <span className="bk-opt-num">${tax.toFixed(2)}</span>
                  </div>
                )}
                <div className="bk-options-row total">
                  <span className="bk-opt-name">Total</span>
                  <span></span>
                  <span></span>
                  <span className="bk-opt-num">${totalAmount.toFixed(2)} {currency}</span>
                </div>
              </div>

              {/* Where the total came from. A booking predating SAM-193 has no server-priced amount,
                  and its total is the sum of what the client sent — worth saying rather than
                  presenting both the same way. */}
              {!amountIsServerPriced && (
                <p className="bk-note">
                  This total is the sum of the options as submitted; no server-priced amount was
                  recorded for this booking.
                </p>
              )}

              {/* The payment itself. Only rendered when there is something to show, so a free booking
                  does not display a row of empty fields. */}
              {(booking.payment_gateway || booking.payment_reference || booking.payment_auth_code
                || booking.role_used) && (
                <div className="bk-kv">
                  {booking.payment_gateway && (
                    <div><span>Gateway</span><span>{booking.payment_gateway}</span></div>
                  )}
                  {booking.payment_reference && (
                    <div><span>Reference</span><span className="bk-mono">{booking.payment_reference}</span></div>
                  )}
                  {booking.payment_auth_code && (
                    <div><span>Auth code</span><span className="bk-mono">{booking.payment_auth_code}</span></div>
                  )}
                  {booking.role_used && (
                    <div><span>Rate applied</span><span>{String(booking.role_used)}</span></div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* SAM-197 — why a cancellation was free or penalised. Shown only when it was evaluated:
              an older cancellation has no record, and an empty block would read as "no penalty". */}
          {isCancelled && booking.cancellation && (
            <section className="bk-section">
              <div className="bk-section-title">Cancellation</div>
              <div className="bk-kv">
                <div>
                  <span>Policy</span>
                  <span>{booking.cancellation.within_policy ? 'Cancelled in time' : 'Cancelled late'}</span>
                </div>
                {booking.cancellation.threshold_minutes != null && (
                  <div>
                    <span>Limit</span>
                    <span>{Math.round(booking.cancellation.threshold_minutes / 60)}h before</span>
                  </div>
                )}
                {booking.cancellation.minutes_before != null && (
                  <div>
                    <span>Cancelled</span>
                    <span>
                      {booking.cancellation.minutes_before < 0
                        ? 'after it started'
                        : `${Math.round(booking.cancellation.minutes_before / 60)}h before`}
                    </span>
                  </div>
                )}
                <div>
                  <span>Penalty</span>
                  <span>{booking.cancellation.penalty_applies ? 'Applies' : 'None'}</span>
                </div>
              </div>
            </section>
          )}

          {/* Timeline */}
          <section className="bk-section">
            <div className="bk-section-header">
              <span className="bk-section-title">Activity</span>
            </div>
            <div className="bk-timeline">
              {booking.status_id === 2 && (
                <div className="bk-tl-item">
                  <span className="bk-tl-dot" />
                  <span className="bk-tl-label">Booking confirmed</span>
                  <span className="bk-tl-time">{formatDateTime(booking.updated_at || booking.created_at)}</span>
                </div>
              )}
              {booking.payment && (
                <div className="bk-tl-item">
                  <span className="bk-tl-dot muted" />
                  <span className="bk-tl-label">Payment received</span>
                  <span className="bk-tl-time">{formatDateTime(booking.created_at)}</span>
                </div>
              )}
              <div className="bk-tl-item">
                <span className="bk-tl-dot muted" />
                <span className="bk-tl-label">
                  Booking created{booking.channel ? ` from ${booking.channel}` : ''}
                </span>
                <span className="bk-tl-time">{formatDateTime(booking.created_at)}</span>
              </div>
            </div>
          </section>

          {/* Access / QR */}
          <section className="bk-section">
            <div className="bk-section-header">
              <span className="bk-section-title">Access</span>
            </div>
            <div className="bk-linked-passes" role="group" aria-label="Linked passes">
              {linkedPassesQuery.isLoading ? (
                <div className="bk-linked-pass-empty">Loading pass…</div>
              ) : linkedPasses.length === 0 ? (
                <div className="bk-linked-pass-empty">No pass linked to this booking yet.</div>
              ) : (
                linkedPasses.map((p) => {
                  const guest = [p.main_guest?.name, p.main_guest?.last_name].filter(Boolean).join(' ') || 'Guest';
                  return (
                    <div key={p.id} className="bk-linked-pass">
                      <QrCode size={16} className="bk-linked-pass-icon" />
                      <div className="bk-linked-pass-body">
                        <div className="bk-linked-pass-title">{p.type_name || 'Pass'} · {guest}</div>
                        <div className="bk-linked-pass-meta">
                          {p.status_name || 'Status unknown'}
                          {p.start_date ? ` · ${formatDate(p.start_date)} ${formatTime(p.start_date)}` : ''}
                          {p.hik_pin ? ` · PIN ${p.hik_pin}` : ''}
                        </div>
                      </div>
                      <Link className="bk-linked-pass-link" to={passActivityLink(String(p.business_id || booking.business_id), String(p.id))}>
                        View pass
                      </Link>
                    </div>
                  );
                })
              )}
            </div>
            {!asFrontDesk && (
            <div className="bk-footer-right" style={{ justifyContent: 'flex-start' }}>
              <button
                type="button"
                className="bk-btn bk-btn-secondary"
                disabled={reissueAccess.isPending}
                onClick={() => reissueAccess.mutate(booking.id)}
              >
                <QrCode size={14} /> {reissueAccess.isPending ? 'Reissuing…' : 'Reissue QR'}
              </button>
              <button
                type="button"
                className="bk-btn bk-btn-secondary"
                disabled={revokeAccess.isPending}
                onClick={() => revokeAccess.mutate(booking.id)}
              >
                {revokeAccess.isPending ? 'Invalidating…' : 'Invalidate QR'}
              </button>
            </div>
            )}
          </section>

          {/* Manage — status + notes */}
          <section className="bk-section">
            <div className="bk-section-header">
              <span className="bk-section-title">Manage</span>
              <span className="bk-section-action">Visible to staff only</span>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: 'var(--ink-500)', fontWeight: 500 }}>Status</span>
              <select
                className="bk-status-select"
                value={statusId}
                onChange={(e) => setStatusId(Number(e.target.value))}
                disabled={isSaving}
              >
                {BOOKING_STATUS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>

            <textarea
              className="bk-notes-box"
              placeholder="Add internal notes about this booking…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </section>

          {/* Footer */}
          <div className="bk-detail-footer">
            <button
              type="button"
              className="bk-danger-link"
              disabled={isCancelled || cancelOnBehalf.isPending}
              onClick={() => setDialog({ type: 'cancel' })}
            >
              <Ban /> Cancel on behalf of guest
            </button>

            <div className="bk-footer-right">
              {showVoidButton && (
                <button
                  type="button"
                  className="bk-btn bk-btn-secondary"
                  disabled={voidBooking.isPending}
                  onClick={() => setDialog({ type: 'void' })}
                >
                  <ShieldOff size={14} /> Void
                </button>
              )}
              {showRefundButton && (
                <button
                  type="button"
                  className="bk-btn bk-btn-secondary"
                  disabled={refundBooking.isPending}
                  onClick={() => setDialog({ type: 'refund' })}
                >
                  <RotateCcw size={14} /> Refund
                </button>
              )}
              {!asFrontDesk && !isCompleted && !isCancelled && (
                <button
                  type="button"
                  className="bk-btn bk-btn-secondary"
                  disabled={isSaving}
                  onClick={() => setDialog({ type: 'no_show' })}
                >
                  Mark as no-show
                </button>
              )}
              {dirty ? (
                <button
                  type="button"
                  className="bk-btn bk-btn-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
              ) : (
                !isCompleted && !isCancelled && (
                  <button
                    type="button"
                    className="bk-btn bk-btn-primary"
                    disabled={isSaving}
                    onClick={() => setDialog({ type: 'complete' })}
                  >
                    <CheckCircle2 />
                    Mark completed
                  </button>
                )
              )}
            </div>
          </div>
        </>
      )}

      {/* Confirm dialogs */}
      {dialog && (
        <div className="bk-confirm-overlay" onClick={(e) => e.target === e.currentTarget && closeDialog()}>
          <div className="bk-confirm-box">
            {dialog.type === 'cancel' && (
              <>
                <div className="bk-confirm-title">Cancel this booking?</div>
                <div className="bk-confirm-body">
                  This will cancel the booking for <strong>{customer}</strong> on the guest's behalf.
                  {cancelPolicy.isLoading && <p className="bk-note">Loading cancellation policy…</p>}
                  {cancelPolicy.data?.describe_for_guest && (
                    <p className="bk-note">{cancelPolicy.data.describe_for_guest}</p>
                  )}
                  <label className="bk-field-label" htmlFor="bk-cancel-reason">Reason</label>
                  <textarea
                    id="bk-cancel-reason"
                    className="bk-notes-box"
                    placeholder="Why is this booking being cancelled?"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
                <div className="bk-confirm-footer">
                  <button className="bk-btn bk-btn-secondary" type="button" onClick={closeDialog}>Back</button>
                  <button
                    className="bk-btn bk-btn-danger"
                    type="button"
                    disabled={!reason.trim() || cancelOnBehalf.isPending}
                    onClick={handleCancelOnBehalf}
                  >
                    {cancelOnBehalf.isPending ? 'Cancelling…' : 'Cancel booking'}
                  </button>
                </div>
              </>
            )}

            {dialog.type === 'complete' && (
              <>
                <div className="bk-confirm-title">Mark booking as completed?</div>
                <div className="bk-confirm-body">
                  Confirm this booking is done. <strong>{customer}</strong> attended and the service was provided.
                </div>
                <div className="bk-confirm-footer">
                  <button className="bk-btn bk-btn-secondary" type="button" onClick={closeDialog}>Cancel</button>
                  <button className="bk-btn bk-btn-primary" type="button" onClick={() => handleStatusChange(BOOKING_STATUS_COMPLETED)}>
                    Mark completed
                  </button>
                </div>
              </>
            )}

            {dialog.type === 'no_show' && (
              <>
                <div className="bk-confirm-title">Mark guest as no-show?</div>
                <div className="bk-confirm-body">
                  Flag <strong>{customer}</strong> as a no-show. They didn't attend at the reserved time.
                  <label className="bk-field-label" htmlFor="bk-noshow-reason">Reason (optional)</label>
                  <textarea
                    id="bk-noshow-reason"
                    className="bk-notes-box"
                    placeholder="Optional note"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
                <div className="bk-confirm-footer">
                  <button className="bk-btn bk-btn-secondary" type="button" onClick={closeDialog}>Cancel</button>
                  <button className="bk-btn bk-btn-primary" type="button" disabled={markNoShow.isPending} onClick={handleNoShow}>
                    {markNoShow.isPending ? 'Saving…' : 'Mark no-show'}
                  </button>
                </div>
              </>
            )}

            {dialog.type === 'void' && (
              <>
                <div className="bk-confirm-title"><AlertTriangle size={16} /> Void this authorization?</div>
                <div className="bk-confirm-body">
                  This reverses the uncaptured authorization for <strong>{customer}</strong>. This action
                  cannot be undone.
                  <label className="bk-field-label" htmlFor="bk-void-reason">Reason</label>
                  <textarea
                    id="bk-void-reason"
                    className="bk-notes-box"
                    placeholder="Why is this authorization being voided?"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
                <div className="bk-confirm-footer">
                  <button className="bk-btn bk-btn-secondary" type="button" onClick={closeDialog}>Back</button>
                  <button className="bk-btn bk-btn-danger" type="button" disabled={!reason.trim() || voidBooking.isPending} onClick={handleVoid}>
                    {voidBooking.isPending ? 'Voiding…' : 'Void authorization'}
                  </button>
                </div>
              </>
            )}

            {dialog.type === 'refund' && (
              <>
                <div className="bk-confirm-title"><AlertTriangle size={16} /> Refund this booking?</div>
                <div className="bk-confirm-body">
                  Captured amount: <strong>${chargedAmount != null ? chargedAmount.toFixed(2) : '—'} {currency}</strong>.
                  This action cannot be undone.
                  <label className="bk-field-label" htmlFor="bk-refund-amount">Amount (leave blank for full refund)</label>
                  <input
                    id="bk-refund-amount"
                    className="bk-notes-box"
                    style={{ height: 'auto', padding: '8px 12px' }}
                    placeholder={chargedAmount != null ? chargedAmount.toFixed(2) : '0.00'}
                    value={refundAmount}
                    onChange={(e) => { setRefundAmount(e.target.value); setRefundError(null); }}
                  />
                  <label className="bk-field-label" htmlFor="bk-refund-reason">Reason</label>
                  <textarea
                    id="bk-refund-reason"
                    className="bk-notes-box"
                    placeholder="Why is this booking being refunded?"
                    value={reason}
                    onChange={(e) => { setReason(e.target.value); setRefundError(null); }}
                  />
                  {refundError && <p className="bk-note bk-note-error">{refundError}</p>}
                  {/* Confirmation repeats amount and reason, per SAM-553's requirement that an
                      irreversible action's confirmation echoes exactly what will be sent. */}
                  <p className="bk-note">
                    Confirming will refund{' '}
                    <strong>{refundAmount.trim() ? `$${refundAmount.trim()}` : 'the full captured amount'}</strong>
                    {reason.trim() && <> for reason: “{reason.trim()}”</>}.
                  </p>
                </div>
                <div className="bk-confirm-footer">
                  <button className="bk-btn bk-btn-secondary" type="button" onClick={closeDialog}>Back</button>
                  <button className="bk-btn bk-btn-danger" type="button" disabled={refundBooking.isPending} onClick={handleRefund}>
                    {refundBooking.isPending ? 'Refunding…' : 'Issue refund'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetailPanel;
